<template>
  <div class="hcl-form-element" v-if="!definitionField.field.hidden"
    :data-form-element-type="definitionField.field.type" :data-form-element-name="definitionField.field.name">
    <div class="form-group">
      <label :for="fieldName" v-if="definitionField.field.title !== ''">
        {{ definitionField.field.title }}
        <span v-if="props.definitionField.field.required" class="required">*</span>
        <hcl-form-label-info v-if="labelHasInfo" :labelInfo="props.definitionField.field.info"></hcl-form-label-info>
      </label>
      <div class="input-group">
        <hcl-form-element-input-group-addon v-if="addonsPre && addonsPre.length > 0" :addons="addonsPre" type="pre">
        </hcl-form-element-input-group-addon>
        <component :is="componentName" :definitionField="props.definitionField" :dataField="props.dataField"
          :dataLookup="props.dataLookup" v-on:hcl-tiles-tile-change-height="onTilesTileChangeHeight"
          v-on:hcl-form-input-enter-key="onFormInputEnterKey"></component>
        <hcl-form-element-input-group-addon v-if="addonsPost && addonsPost.length > 0" :addons="addonsPost" type="post">
        </hcl-form-element-input-group-addon>
        <div class="valid-feedback" v-if="
          props.definitionField.field.validation.valid &&
          props.definitionField.field.validation.valid !== ''
        ">{{ props.definitionField.field.validation.valid }}</div>
        <div class="invalid-feedback" v-if="
          props.definitionField.field.validation.invalid &&
          props.definitionField.field.validation.invalid !== ''
        ">{{ props.definitionField.field.validation.invalid }}</div>
      </div>
      <small class="form-text text-muted"
        v-if="props.definitionField.field.help && props.definitionField.field.help !== ''">
        {{
            props.definitionField.field.help
        }}
      </small>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useElement } from '@/components/FormElement/element'

import HclFormElementEmpty from '@/components/FormElementEmpty/FormElementEmpty.vue'
import HclFormElementText from '@/components/FormElementText/FormElementText.vue'
import HclFormElementHtml from '@/components/FormElementHtml/FormElementHtml.vue'
import HclFormElementTextarea from '@/components/FormElementTextarea/FormElementTextarea.vue'
import HclFormElementDate from '@/components/FormElementDate/FormElementDate.vue'
import HclFormElementSingleSelect from '@/components/FormElementSingleSelect/FormElementSingleSelect.vue'
import HclFormElementMultiSelect from '@/components/FormElementMultiSelect/FormElementMultiSelect.vue'
import HclFormElementInputGroupAddon from '@/components/FormElementInputGroupAddon/FormElementInputGroupAddon.vue'
import HclFormElementToggle from '@/components/FormElementToggle/FormElementToggle.vue'
import HclFormElementCheckboxes from '@/components/FormElementCheckboxes/FormElementCheckboxes.vue'
import HclFormElementRadios from '@/components/FormElementRadios/FormElementRadios.vue'
import HclFormElementSkillLevels from '@/components/FormElementSkillLevels/FormElementSkillLevels.vue'
import HclFormLabelInfo from '@/components/FormLabelInfo/FormLabelInfo.vue'

interface Props {
  /**
   * Definition as an JS object.
   */
  definitionField: any
  dataField: any
  dataLookup: any
}

const props = withDefaults(defineProps<Props>(), {
  definition: undefined,
  dataField: undefined,
  dataLookup: undefined
})

const {
  fieldName,
  addonsPre,
  addonsPost,
  labelHasInfo,
} = useElement(props)

const componentName = computed(() => {
  if (props.definitionField.field.type === 'date') {
    return HclFormElementDate
  }

  if (props.definitionField.field.type === 'empty') {
    return HclFormElementEmpty
  }

  if (props.definitionField.field.type === 'html') {
    return HclFormElementHtml
  }

  if (
    props.definitionField.field.type === 'text' &&
    props.definitionField.field.render === 'textarea'
  ) {
    return HclFormElementTextarea
  }

  if (props.definitionField.field.type === 'select-single') {
    return HclFormElementSingleSelect
  }

  if (props.definitionField.field.type === 'select-multiple') {
    return HclFormElementMultiSelect
  }

  if (props.definitionField.field.type === 'toggle') {
    return HclFormElementToggle
  }

  if (props.definitionField.field.type === 'checkboxes') {
    return HclFormElementCheckboxes
  }

  if (props.definitionField.field.type === 'radios') {
    return HclFormElementRadios
  }

  if (props.definitionField.field.type === 'skill-levels') {
    return HclFormElementSkillLevels
  }

  return HclFormElementText
})

const emit = defineEmits(['hcl-tiles-tile-change-height', 'hcl-form-input-enter-key'])

const onTilesTileChangeHeight = (event: any) => {
  emit('hcl-tiles-tile-change-height', event)
}

const onFormInputEnterKey = (event) => {
  emit('hcl-form-input-enter-key', event)
}
</script>

<style lang="scss">
.hcl-form-element {
  .form-group {
    .required {
      color: #d83c31;
    }
  }

  .input-group {
    input {
      background-color: white;
    }

    flex-wrap: nowrap;
  }
}
</style>
